/**
 * SiteGate - Simple production-only login gate
 *
 * Purpose: Protect the site from public access before launch
 *
 * How it works:
 * - In DEV mode: Gate is always skipped (no login needed)
 * - In PROD mode: Gate shows by default
 * - Uses sessionStorage so users stay logged in until browser closes
 *
 * To disable when ready to launch:
 * 1. Set VITE_SITE_UNLOCKED=true in Vercel environment variables, OR
 * 2. Delete this file and remove from App.tsx
 */
import React, { useState, useEffect, useCallback } from 'react';

interface SiteGateProps {
  children: React.ReactNode;
}

const ST_LOGO_URL = '/st-logo.png';

// Simple credentials - change these if needed
const GATE_USERNAME = 'admin';
const GATE_PASSWORD = 'admin';
const STORAGE_KEY = 'stellartrail-site-unlocked';

export const SiteGate: React.FC<SiteGateProps> = ({ children }) => {
  const [isUnlocked, setIsUnlocked] = useState<boolean | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // In production: locked by default unless VITE_SITE_UNLOCKED=true
  // In development: always unlocked
  const isLocked = import.meta.env.PROD && import.meta.env.VITE_SITE_UNLOCKED !== 'true';

  // Check sessionStorage on mount
  useEffect(() => {
    // If not locked, immediately unlock
    if (!isLocked) {
      setIsUnlocked(true);
      return;
    }

    // Check if already unlocked this session
    const unlocked = sessionStorage.getItem(STORAGE_KEY) === 'true';
    setIsUnlocked(unlocked);
  }, [isLocked]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (username === GATE_USERNAME && password === GATE_PASSWORD) {
      sessionStorage.setItem(STORAGE_KEY, 'true');
      setIsUnlocked(true);
    } else {
      setError('Invalid credentials');
      setPassword('');
    }
  }, [username, password]);

  // Loading state while checking
  if (isUnlocked === null) {
    return (
      <div className="min-h-screen bg-st-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-st-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Site is unlocked, render children
  if (isUnlocked) {
    return <>{children}</>;
  }

  // Show login gate
  return (
    <div className="min-h-screen bg-gradient-to-b from-st-dark to-st-darker particles flex items-center justify-center p-4">
      {/* Subtle ambient glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
      >
        <div className="absolute bottom-1/4 right-1/3 w-64 h-64 bg-st-primary/5 rounded-full blur-3xl" />
        <div className="absolute top-1/3 left-1/4 w-48 h-48 bg-st-primary/3 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-sm">
        <div className="bg-st-card/95 backdrop-blur-sm rounded-2xl border border-slate-700/80 p-8 shadow-2xl shadow-black/20">
          {/* Logo + Alpha Badge */}
          <div className="text-center mb-6">
            <img
              src={ST_LOGO_URL}
              alt=""
              aria-hidden
              className="w-14 h-14 mx-auto mb-4 object-contain"
            />
            <div className="flex items-center justify-center gap-2 mb-2">
              <h1 className="text-2xl font-bold text-white tracking-tight">StellarTrail</h1>
              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full border border-st-primary/50 text-st-primary bg-st-primary/10">
                Alpha
              </span>
            </div>
            <p className="text-st-muted text-sm font-medium leading-relaxed max-w-xs mx-auto">
              Next-gen Trading Collective. Powered by Artificial Intelligence.
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-slate-300 mb-1">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-st-primary focus:border-transparent"
                placeholder="Enter username"
                autoComplete="username"
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-st-primary focus:border-transparent"
                placeholder="Enter password"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div className="text-red-400 text-sm text-center">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-full py-2.5 px-4 bg-st-primary hover:bg-st-primary/90 text-st-dark font-semibold rounded-lg transition-colors"
            >
              Enter Site
            </button>
          </form>

          {/* Footer */}
          <p className="text-center text-slate-500 text-xs mt-6">
            Early access is limited. Reach out for an invite.
          </p>
        </div>
      </div>
    </div>
  );
};
